import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flag } from "lucide-react";
import { useTranslation } from "react-i18next";
import NavigationMenu from "@/components/layout/NavigationMenu";
import FavoriteTests from "@/components/training/FavoriteTests";

const Training = () => {
  const { t } = useTranslation(['training', 'navigation']);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto py-8">
        <div className="flex justify-between mb-4">
          <div className="text-golf-600">
            <Link to="/" className="text-green-600 hover:text-green-700 flex items-center gap-2">
              <Flag className="h-6 w-6" />
              <span>{t('navigation:backToDashboard')}</span>
            </Link>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-4">{t('training:title')}</h1>
          <p className="text-xl text-golf-600">{t('training:subtitle')}</p>
        </motion.div>

        {/* Display Favorite Tests */}
        <FavoriteTests />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="bg-white/50 backdrop-blur h-full">
              <CardContent className="p-6">
                <h2 className="text-2xl font-semibold text-golf-800 mb-4">{t('training:introduction.title')}</h2>
                <p className="text-base leading-relaxed mb-6 text-gray-800">
                  {t('training:introduction.text1')} {t('training:introduction.text2')}
                </p>
                <p className="text-base mb-6 text-gray-800">
                  {t('training:introduction.text3')} {t('training:introduction.text4')}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex justify-center"
          >
            <Card className="bg-white/50 backdrop-blur h-fit w-full max-w-md">
              <CardContent className="p-6">
                <h2 className="text-2xl font-semibold text-golf-800 mb-4 text-center">{t('training:levels.title')}</h2>
                <div className="space-y-4">
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-4 bg-white rounded-lg shadow-sm border border-golf-100"
                  >
                    <Link to="/training/level3" className="flex items-center">
                      <Flag className="h-5 w-5 mr-2 text-green-600" />
                      <h3 className="font-semibold text-base text-golf-800 mb-2">{t('training:levels.level3.title')}</h3>
                    </Link>
                    <p className="text-sm text-gray-700">{t('training:levels.level3.description')}</p>
                    <div className="mt-2">
                      <Button asChild size="sm" className="bg-golf-600 hover:bg-golf-700 text-white w-full">
                        <Link to="/training/level3">
                          {t('training:levels.level3.button')}
                        </Link>
                      </Button>
                    </div>
                  </motion.div>

                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="p-4 bg-white rounded-lg shadow-sm border border-golf-100"
                  >
                    <Link to="/training/level2" className="flex items-center">
                      <Flag className="h-5 w-5 mr-2 text-blue-600" />
                      <h3 className="font-semibold text-base text-golf-800 mb-2">{t('training:levels.level2.title')}</h3>
                    </Link>
                    <p className="text-sm text-gray-700">{t('training:levels.level2.description')}</p>
                    <div className="mt-2">
                      <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white w-full">
                        <Link to="/training/level2">
                          {t('training:levels.level2.button')}
                        </Link>
                      </Button>
                    </div>
                  </motion.div>

                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="p-4 bg-white rounded-lg shadow-sm border border-golf-100"
                  >
                    <Link to="/training/level1" className="flex items-center">
                      <Flag className="h-5 w-5 mr-2 text-orange-600" />
                      <h3 className="font-semibold text-base text-golf-800 mb-2">{t('training:levels.level1.title')}</h3>
                    </Link>
                    <p className="text-sm text-gray-700">{t('training:levels.level1.description')}</p>
                    <div className="mt-2">
                      <Button asChild size="sm" className="bg-orange-600 hover:bg-orange-700 text-white w-full">
                        <Link to="/training/level1">
                          {t('training:levels.level1.button')}
                        </Link>
                      </Button>
                    </div>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <Card className="bg-white/50 backdrop-blur">
              <CardContent className="p-6">
                <h2 className="text-2xl font-semibold text-golf-800 mb-4">{t('training:shortGame.title')}</h2>
                <div className="space-y-4">
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/training/putting">
                      {t('training:shortGame.putting')}
                    </Link>
                  </Button>
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/training/chipping">
                      {t('training:shortGame.chipping')}
                    </Link>
                  </Button>
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/training/wedging">
                      {t('training:shortGame.wedging')}
                    </Link>
                  </Button>
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/training/bunker">
                      {t('training:shortGame.bunker')}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/50 backdrop-blur">
              <CardContent className="p-6">
                <h2 className="text-2xl font-semibold text-golf-800 mb-4">{t('training:longGame.title')}</h2>
                <div className="space-y-4">
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/training/swing">
                      {t('training:longGame.iron')}
                    </Link>
                  </Button>
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/training/driver">
                      {t('training:longGame.driver')}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/50 backdrop-blur">
              <CardContent className="p-6">
                <h2 className="text-2xl font-semibold text-golf-800 mb-4">{t('training:courseStats.title')}</h2>
                <div className="space-y-4">
                  <Button asChild className="w-full bg-golf-600 hover:bg-golf-700 text-white">
                    <Link to="/course-stats">
                      {t('training:courseStats.button')}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Training;
